/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file FilterComponent.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import ArrowDropDownRounded from '@material-ui/icons/ArrowDropDownRounded';
import ArrowDropUpRounded from '@material-ui/icons/ArrowDropUpRounded';
import Button from '@material-ui/core/Button';
import MenuList from '@material-ui/core/MenuList';
import Popover from '@material-ui/core/Popover';
import { withStyles, useTheme } from '@material-ui/core/styles';
import { injectIntl, intlShape } from 'react-intl';

import FilterActionButton from './FilterActionButton';
import FilterActionButtonDefault from './FilterActionButtonDefault';
import FilterListComponent from './FilterListComponent';
import ResourceUtils from '../../utils/ResourceUtils';
import ScrollContainer from '../common/ScrollContainer';
import { CONTENT_TYPE_KEYS } from '../../common/constants';
import messages from '../../../translations/defaultMessages';

const styles = (theme) => ({
  button: {
    background: theme.color.background,
    color: theme.color.textPrimary,
    border: `1px solid ${theme.color.divider}`,
    borderRadius: 4,
    boxShadow: '0px 1px 4px rgb(28 34 46 / 5%), 0px 2px 12px rgb(28 34 46 / 6%)',
    fontFamily: 'Hind',
    fontStyle: 'normal',
    fontWeight: 'normal',
    justifyContent: 'space-between',
    margin: 0,
    padding: '11px 16px',
    '&:hover': {
      background: theme.color.background,
      color: theme.color.textPrimary
    },
    '&:disabled': {
      background: theme.color.toggle
    }
  },
  menuList: {
    padding: 0
  },
  filterActionContainer: {
    background: theme.color.background,
    display: 'flex',
    justifyContent: 'space-between',
    padding: 16
  },
  selectedContentTypeCount: {
    background: '#4F5561',
    borderRadius: 4,
    color: '#FEFEFE',
    fontFamily: 'Hind',
    fontSize: 16,
    fontStyle: 'normal',
    padding: '2px 6px 0px',
    marginRight: 6
  },
  selectedContentTypeCountDisabled: {
    background: '#F7F9FD',
    borderRadius: 4,
    color: '#9BA1AD',
    fontFamily: 'Hind',
    fontSize: 16,
    fontStyle: 'normal',
    padding: '2px 6px 0px',
    marginRight: 6
  }
});

const FilterComponent = ({
  availableContentType,
  bulkDownloadStatus,
  classes,
  filteredContentType,
  intl,
  isCurrentTabEmpty,
  onFilterActionClick,
  onFilterDropDownOpen
}) => {
  const [open, setOpen] = useState(false);
  const [selectedContentType, setContentType] = useState([]);
  const anchorRef = React.useRef(null);
  const { formatMessage } = intl;
  /**
   * Handles the drop down open and close
   */
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  /**
   * Hnadles the dropdown close
   * @param {*} event
   * @returns
   */
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  /**
   * Handles menu item click
   * @param {*} event
   * @param {*} contentLabel
   */
  const handleMenuClick = (event, contentLabel) => {
    const previousSelectedContentType = [...selectedContentType];
    const indexValue = previousSelectedContentType.indexOf(contentLabel);

    if (indexValue > -1) {
      previousSelectedContentType.splice(indexValue, 1);
    } else {
      previousSelectedContentType.push(contentLabel);
    }
    setContentType(previousSelectedContentType);
  };

  /**
   * Handles apply button click
   * @param {*} event
   */
  const handleApplyClick = (event) => {
    handleClose(event);
    onFilterActionClick(selectedContentType);
  };

  /**
   * Handles reset button click
   * @param {*} event
   */
  const handleResetClick = (event) => {
    setContentType([]);
    onFilterActionClick([]);
    handleClose(event);
  };
  const prevOpen = React.useRef(open);
  const disableFilterActionButton = selectedContentType.length === 0;
  const menuHeight = () => {
    const parentEle = anchorRef;

    if (parentEle && parentEle.current) {
      const parentEleBottomPosition = parentEle.current.getBoundingClientRect().bottom + 40;
      const menuHeightVal = window.innerHeight - parentEleBottomPosition;

      if (parentEleBottomPosition > window.innerHeight) {
        return window.innerHeight;
      }

      return menuHeightVal;
    }

    return null;
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    if (open === true) {
      onFilterDropDownOpen(true);
      setContentType(filteredContentType);
    } else {
      onFilterDropDownOpen(false);
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Fragment>
      <Button
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        className={classes.button}
        color="default"
        disabled={isCurrentTabEmpty || ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)}
        fullWidth
        onClick={handleToggle}
        ref={anchorRef}
        size="large"
        variant="outlined"
      >
        <span>
          {!open && filteredContentType.length > 0 ?
            <span>
              <span
                className={ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)
                  ? classes.selectedContentTypeCountDisabled : classes.selectedContentTypeCount}
              >
                {filteredContentType.length}
              </span>
              {!open && filteredContentType.length > 1
                ? formatMessage(messages.resourceTypes) : formatMessage(messages.resourceType)}
            </span>
            : formatMessage(messages.filterByResourceType)}
        </span>
        {open ? <ArrowDropUpRounded /> : <ArrowDropDownRounded />}
      </Button>
      <Popover
        anchorEl={anchorRef.current}
        open={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          style: {
            maxHeight: menuHeight(),
            width: anchorRef && anchorRef.current && anchorRef.current.offsetWidth,
            borderRadius: 4,
            backgroundColor: useTheme().color.background,
            border: `1px solid ${useTheme().color.divider}`
          }
        }

        }
        onClose={handleClose}
      >
        <ScrollContainer computedMaxHeight={menuHeight()}>
          <div>
            <MenuList
              autoFocusItem={open}
              className={classes.menuList}
              id="menu-list-grow"
            >
              {availableContentType.map(contentType => (<FilterListComponent
                contentTypes={contentType}
                key={contentType[CONTENT_TYPE_KEYS.LABEL]}
                onMenuClick={handleMenuClick}
                selectedContentType={selectedContentType}
              />))}
            </MenuList>
            <div className={classes.filterActionContainer}>
              <FilterActionButtonDefault
                color="default"
                disabled={disableFilterActionButton}
                onClick={handleResetClick}
                size="large"
                variant="outlined"
              >
                {formatMessage(messages.clearAll)}
              </FilterActionButtonDefault>
              <FilterActionButton
                color="primary"
                disabled={disableFilterActionButton}
                onClick={handleApplyClick}
                size="large"
                variant="outlined"
              >
                {formatMessage(messages.apply)}
              </FilterActionButton>
            </div>
          </div>
        </ScrollContainer>
      </Popover>
    </Fragment>

  );
};

FilterComponent.propTypes = {
  availableContentType: PropTypes.array,
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  filteredContentType: PropTypes.array.isRequired,
  intl: intlShape,
  isCurrentTabEmpty: PropTypes.bool.isRequired,
  onFilterActionClick: PropTypes.func.isRequired,
  onFilterDropDownOpen: PropTypes.func.isRequired
};
FilterComponent.defaultProps = {
  availableContentType: [],
  intl: {}
};

export default withStyles(styles)(injectIntl(FilterComponent));
